import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Select, Button, Space } from 'antd';
import { FileAddOutlined, UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { testService } from '../../services/testService';
import { labTestSelectOptions, labOrderEntries } from '../../utils/labOrder';
import { patientService } from '../../services';
import AttachmentPreview from '../../components/AttachmentPreview';
import { attachmentsOf } from '../../utils/attachments';
import './TestsAttachments.css';

const EMPTY_TEST_FORM = {
    testType: 'blood',
    catalogIds: [],
    testNames: [],
    labName: '',
    notes: ''
};

function TestsAttachments() {
    const { t } = useTranslation();
    const { patientId } = useParams();
    const [activeTab, setActiveTab] = useState('tests');
    const [tests, setTests] = useState([]);
    const [attachments, setAttachments] = useState([]);
    const [patients, setPatients] = useState([]);
    const [catalog, setCatalog] = useState([]);
    const [selectedPatientId, setSelectedPatientId] = useState(patientId || '');
    const [showTestModal, setShowTestModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);

    const [testForm, setTestForm] = useState(EMPTY_TEST_FORM);

    // Branch doctors order from the branch lab's catalog; independent doctors
    // keep typing the test names themselves.
    const usesCatalog = catalog.length > 0;

    useEffect(() => {
        loadPatients();
        loadCatalog();
    }, []);

    useEffect(() => {
        if (selectedPatientId) {
            loadTests();
            loadAttachments();
        }
    }, [selectedPatientId]);

    const loadPatients = async () => {
        try {
            const response = await patientService.getPatients();
            setPatients(response.data || []);
        } catch (error) {
            console.error('Error loading patients:', error);
        }
    };

    const loadCatalog = async () => {
        try {
            const response = await testService.getBranchCatalog();
            setCatalog(response.data || []);
        } catch (error) {
            // Non-fatal: the order form falls back to free-text test names.
            console.error('Error loading lab catalog:', error);
        }
    };

    const loadTests = async () => {
        try {
            const data = await testService.getPatientTests(selectedPatientId);
            setTests(data);
        } catch (error) {
            console.error('Error loading tests:', error);
        }
    };

    const loadAttachments = async () => {
        try {
            const data = await testService.getPatientAttachments(selectedPatientId);
            setAttachments(data);
        } catch (error) {
            console.error('Error loading attachments:', error);
        }
    };

    const handleTestSubmit = async (e) => {
        e.preventDefault();
        if (!selectedPatientId) {
            alert('Please select a patient first');
            return;
        }

        // One order per selected test, sent to the lab in a single request. A
        // picked test is ordered by reference so the lab gets the catalog's or the
        // library's own wording; a typed one is sent as the doctor wrote it.
        const tests = usesCatalog
            ? labOrderEntries(testForm.catalogIds, catalog)
            : testForm.testNames.map((testName) => ({ testName, testType: testForm.testType }));

        if (tests.length === 0) {
            alert(t('selectTest') || 'Please select at least one test');
            return;
        }

        try {
            await testService.orderTests({
                patientId: selectedPatientId,
                labName: testForm.labName,
                notes: testForm.notes,
                tests
            });
            setShowTestModal(false);
            setTestForm(EMPTY_TEST_FORM);
            loadTests();
        } catch (error) {
            alert('Error ordering test: ' + error.message);
        }
    };

    const handleFileUpload = async (e) => {
        e.preventDefault();
        if (!selectedPatientId) {
            alert('Please select a patient first');
            return;
        }
        const formData = new FormData(e.target);
        formData.append('patientId', selectedPatientId);

        try {
            await testService.uploadAttachment(formData);
            setShowUploadModal(false);
            loadAttachments();
            e.target.reset();
        } catch (error) {
            alert('Error uploading file: ' + error.message);
        }
    };

    const getStatusBadgeClass = (status) => {
        const classes = {
            ordered: 'status-ordered',
            pending: 'status-pending',
            completed: 'status-completed',
            cancelled: 'status-cancelled'
        };
        return classes[status] || '';
    };

    return (
        <div className="tests-attachments-container">
            <div className="page-header">
                <h1>{t('testsAndAttachments')}</h1>
                <div className="header-actions">
                    <div className="form-group" style={{ marginRight: '15px', minWidth: '250px' }}>
                        <Select
                            showSearch
                            value={selectedPatientId || undefined}
                            onChange={(value) => setSelectedPatientId(value)}
                            placeholder={t('searchAndSelectPatient')}
                            optionFilterProp="label"
                            style={{ width: '100%' }}
                        >
                            {patients.map((patient) => (
                                <Select.Option
                                    key={patient._id}
                                    value={patient._id}
                                    label={`${patient.name} - ${patient.phoneNumber}`}
                                >
                                    {patient.name} - {patient.phoneNumber}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    <Space>
                        <Button
                            type="primary"
                            icon={<FileAddOutlined />}
                            onClick={() => setShowTestModal(true)}
                            disabled={!selectedPatientId}
                            size="large"
                        >
                            {t('orderTest')}
                        </Button>
                        <Button
                            icon={<UploadOutlined />}
                            onClick={() => setShowUploadModal(true)}
                            disabled={!selectedPatientId}
                            size="large"
                        >
                            {t('uploadFile')}
                        </Button>
                    </Space>
                </div>
            </div>

            <div className="tabs">
                <button
                    className={activeTab === 'tests' ? 'tab active' : 'tab'}
                    onClick={() => setActiveTab('tests')}
                >
                    {t('tests')} ({tests.length})
                </button>
                <button
                    className={activeTab === 'attachments' ? 'tab active' : 'tab'}
                    onClick={() => setActiveTab('attachments')}
                >
                    {t('attachments')} ({attachments.length})
                </button>
                <button
                    className={activeTab === 'gallery' ? 'tab active' : 'tab'}
                    onClick={() => setActiveTab('gallery')}
                >
                    {t('gallery')}
                </button>
            </div>

            {activeTab === 'tests' && (
                <div className="tests-content">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>{t('orderDate')}</th>
                                <th>{t('testType')}</th>
                                <th>{t('testName')}</th>
                                <th>{t('lab')}</th>
                                <th>{t('status')}</th>
                                <th>{t('result')}</th>
                                <th>{t('attachments')}</th>
                                <th>{t('abnormal')}</th>
                                <th>{t('actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tests.map((test) => (
                                <tr key={test._id}>
                                    <td>{new Date(test.orderDate).toLocaleDateString()}</td>
                                    <td>
                                        <span className={`badge ${test.testType}`}>
                                            {test.testType}
                                        </span>
                                    </td>
                                    <td>{test.testName}</td>
                                    <td>{test.labName || '-'}</td>
                                    <td>
                                        <span className={`status-badge ${getStatusBadgeClass(test.status)}`}>
                                            {test.status}
                                        </span>
                                    </td>
                                    <td>
                                        {test.resultValue ||
                                            (attachmentsOf(test).length > 0
                                                ? (t('seeAttachment') || 'See attachment')
                                                : '-')}
                                    </td>
                                    <td>
                                        {/* The lab's own result sheet — read it instead of a retyped value. */}
                                        <AttachmentPreview files={attachmentsOf(test)} size={48} />
                                    </td>
                                    <td>
                                        {test.isAbnormal && <span className="abnormal-flag">⚠️ Abnormal</span>}
                                    </td>
                                    <td>
                                        <button className="btn-sm" onClick={() => {/* View/Edit */ }}>
                                            View
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {tests.length === 0 && (
                        <div className="no-data">No tests ordered yet</div>
                    )}
                </div>
            )}

            {activeTab === 'attachments' && (
                <div className="attachments-content">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>{t('date')}</th>
                                <th>{t('fileName')}</th>
                                <th>{t('type')}</th>
                                <th>{t('category')}</th>
                                <th>{t('size')}</th>
                                <th>{t('description')}</th>
                                <th>{t('actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {attachments.map((attachment) => (
                                <tr key={attachment._id}>
                                    <td>{new Date(attachment.uploadDate).toLocaleDateString()}</td>
                                    <td>{attachment.fileName}</td>
                                    <td>
                                        <span className={`badge ${attachment.fileType}`}>
                                            {attachment.fileType}
                                        </span>
                                    </td>
                                    <td>{attachment.category}</td>
                                    <td>{(attachment.fileSize / 1024).toFixed(2)} KB</td>
                                    <td>{attachment.description || '-'}</td>
                                    <td>
                                        <button className="btn-sm btn-view" onClick={() => {
                                            // View file logic
                                            window.open(`/api/tests/attachments/${attachment._id}`, '_blank');
                                        }}>
                                            View
                                        </button>
                                        <button className="btn-sm btn-delete" onClick={async () => {
                                            if (window.confirm('Delete this attachment?')) {
                                                await testService.deleteAttachment(attachment._id);
                                                loadAttachments();
                                            }
                                        }}>
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {attachments.length === 0 && (
                        <div className="no-data">No attachments uploaded yet</div>
                    )}
                </div>
            )}

            {activeTab === 'gallery' && (
                <div className="gallery-content">
                    <div className="gallery-grid">
                        {attachments
                            .filter(a => a.fileType === 'image' || a.fileType === 'xray' || a.fileType === 'scan')
                            .map((attachment) => (
                                <div key={attachment._id} className="gallery-item" onClick={() => setSelectedImage(attachment)}>
                                    <div className="gallery-thumbnail">
                                        <img src={`/api/tests/attachments/${attachment._id}`} alt={attachment.fileName} />
                                    </div>
                                    <div className="gallery-info">
                                        <div className="gallery-filename">{attachment.fileName}</div>
                                        <div className="gallery-date">{new Date(attachment.uploadDate).toLocaleDateString()}</div>
                                    </div>
                                </div>
                            ))}
                    </div>
                    {attachments.filter(a => a.fileType === 'image' || a.fileType === 'xray').length === 0 && (
                        <div className="no-data">No images uploaded yet</div>
                    )}
                </div>
            )}

            {/* Order Test Modal */}
            {showTestModal && (
                <div className="modal-overlay" onClick={() => setShowTestModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>{t('orderTest')}</h2>
                        <form onSubmit={handleTestSubmit}>
                            {!usesCatalog && (
                                <div className="form-group">
                                    <label>{t('testType')}</label>
                                    <select
                                        value={testForm.testType}
                                        onChange={(e) => setTestForm({ ...testForm, testType: e.target.value })}
                                        required
                                    >
                                        <option value="blood">{t('bloodTest')}</option>
                                        <option value="urine">{t('urineTest')}</option>
                                        <option value="imaging">{t('imaging')}</option>
                                        <option value="biopsy">{t('biopsy')}</option>
                                        <option value="other">{t('other')}</option>
                                    </select>
                                </div>
                            )}
                            <div className="form-group">
                                <label>{t('tests')}</label>
                                {usesCatalog ? (
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        showSearch
                                        value={testForm.catalogIds}
                                        onChange={(catalogIds) => setTestForm({ ...testForm, catalogIds })}
                                        placeholder={t('selectTests') || 'Select one or more tests'}
                                        optionFilterProp="label"
                                        style={{ width: '100%' }}
                                        options={labTestSelectOptions(catalog)}
                                    />
                                ) : (
                                    <Select
                                        mode="tags"
                                        allowClear
                                        value={testForm.testNames}
                                        onChange={(testNames) => setTestForm({ ...testForm, testNames })}
                                        placeholder={t('testNamePlaceholder')}
                                        style={{ width: '100%' }}
                                        tokenSeparators={[',']}
                                    />
                                )}
                                <small style={{ color: '#888' }}>
                                    {t('selectMultipleTestsHint') || 'You can pick several tests at once'}
                                </small>
                            </div>
                            <div className="form-group">
                                <label>{t('labName')}</label>
                                <input
                                    type="text"
                                    value={testForm.labName}
                                    onChange={(e) => setTestForm({ ...testForm, labName: e.target.value })}
                                    placeholder={t('optional')}
                                />
                            </div>
                            <div className="form-group">
                                <label>{t('notes')}</label>
                                <textarea
                                    value={testForm.notes}
                                    onChange={(e) => setTestForm({ ...testForm, notes: e.target.value })}
                                    rows="3"
                                />
                            </div>
                            <div className="modal-actions">
                                <Button onClick={() => setShowTestModal(false)}>
                                    {t('cancel')}
                                </Button>
                                <Button type="primary" htmlType="submit">
                                    {t('orderTest')}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Upload File Modal */}
            {showUploadModal && (
                <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>{t('uploadFile')}</h2>
                        <form onSubmit={handleFileUpload}>
                            <div className="form-group">
                                <label>{t('file')}</label>
                                <input type="file" name="file" required />
                            </div>
                            <div className="form-group">
                                <label>{t('fileType')}</label>
                                <select name="fileType" required>
                                    <option value="image">{t('image')}</option>
                                    <option value="pdf">PDF</option>
                                    <option value="document">{t('document')}</option>
                                    <option value="scan">{t('scan')}</option>
                                    <option value="xray">X-Ray</option>
                                    <option value="other">{t('other')}</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>{t('category')}</label>
                                <select name="category">
                                    <option value="test_result">{t('testResult')}</option>
                                    <option value="prescription">{t('prescription')}</option>
                                    <option value="consent_form">{t('consentForm')}</option>
                                    <option value="report">{t('report')}</option>
                                    <option value="image">{t('image')}</option>
                                    <option value="other">{t('other')}</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>{t('description')}</label>
                                <textarea name="description" rows="3" />
                            </div>
                            <div className="modal-actions">
                                <Button onClick={() => setShowUploadModal(false)}>
                                    {t('cancel')}
                                </Button>
                                <Button type="primary" htmlType="submit">
                                    {t('upload')}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Image Lightbox */}
            {selectedImage && (
                <div className="lightbox-overlay" onClick={() => setSelectedImage(null)}>
                    <div className="lightbox-content">
                        <button className="lightbox-close" onClick={() => setSelectedImage(null)}>×</button>
                        <img src={`/api/tests/attachments/${selectedImage._id}`} alt={selectedImage.fileName} />
                        <div className="lightbox-info">
                            <h3>{selectedImage.fileName}</h3>
                            <p>{selectedImage.description}</p>
                            <p>{new Date(selectedImage.uploadDate).toLocaleString()}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default TestsAttachments;
